export type ImageSize = { width: number; height: number; quality?: number };

const DEFAULT_IMAGE_FORMAT = 'image/png';

export type SupportedImageTypes = 'image/jpeg' | 'image/png';

/* istanbul ignore next */
export const getCanvasFromImageData = (
  imageData: ImageData,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  canvas.getContext('2d')?.putImageData(imageData, 0, 0);
  return canvas;
};

/* istanbul ignore next */
export const getCanvasFromVideoElement = (
  videoElement: HTMLVideoElement,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;
  canvas.getContext('2d')?.drawImage(videoElement, 0, 0);
  return canvas;
};

/* istanbul ignore next */
export const getDataUrl = (canvas: HTMLCanvasElement): string => {
  return canvas.toDataURL(DEFAULT_IMAGE_FORMAT);
};

export type ConvertImageDataToRGB = (imageData: ImageData) => number[];
// getImageData returns array in RGBA, we need RGB
export const convertImageDataToRGB: ConvertImageDataToRGB = (imageData) => {
  const { data } = imageData;

  const rgb = [];
  for (let i = 0; i < data.length; i += 4) {
    rgb.push(data[i], data[i + 1], data[i + 2]);
  }

  return rgb as number[];
};

export const isCanvasBlankImpl = (
  canvas: HTMLCanvasElement,
  canvasContext: CanvasRenderingContext2D | null,
  convertToPixels: ConvertImageDataToRGB = convertImageDataToRGB,
): boolean => {
  if (canvas.width === 0 || canvas.height === 0) {
    return true;
  }

  const midX = Math.floor(canvas.height / 2);
  const imageData = canvasContext?.getImageData(0, midX, canvas.width, 1);
  const pixels = convertToPixels(imageData as ImageData);
  const hasOnlyBlackPixels = pixels.every((pixel) => pixel === 0);
  return hasOnlyBlackPixels;
};

/* istanbul ignore next */
export const isCanvasBlank = (canvas: HTMLCanvasElement): boolean =>
  isCanvasBlankImpl(canvas, canvas.getContext('2d'), convertImageDataToRGB);

/* istanbul ignore next */
const canvasToArrayBuffer = async (
  canvas: HTMLCanvasElement,
  quality: number,
  type: SupportedImageTypes = DEFAULT_IMAGE_FORMAT,
): Promise<ArrayBuffer> => {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        const reader = new FileReader();
        reader.addEventListener('loadend', () =>
          resolve(reader.result as ArrayBuffer),
        );

        reader.readAsArrayBuffer(blob as Blob);
      },
      type,
      quality,
    );
  });
};

export const getDrawImageArguments = (
  canvas: HTMLCanvasElement,
  imageSize: ImageSize,
): [
  CanvasImageSource,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
] => {
  const xscale = imageSize.width / canvas.width;
  const yscale = imageSize.height / canvas.height;
  const scale = Math.max(xscale, yscale);
  const scaledCanvasWidth = canvas.width * scale;
  const scaledCanvasHeight = canvas.height * scale;
  return [
    canvas,
    0,
    0,
    canvas.width,
    canvas.height,
    (imageSize.width - scaledCanvasWidth) * 0.5,
    (imageSize.height - scaledCanvasHeight) * 0.5,
    scaledCanvasWidth,
    scaledCanvasHeight,
  ];
};

export const getCanvasToArrayBufferArguments = (
  canvas: HTMLCanvasElement,
  imageSize: ImageSize | undefined,
  getResizedCanvas: GetResizedCanvas,
  imageType: SupportedImageTypes = DEFAULT_IMAGE_FORMAT,
): [
  resizedCanvas: HTMLCanvasElement,
  quality: number,
  imageType: SupportedImageTypes,
] => [
  // TODO: fix case when width and height are not defined but quality is. Now there is no ability to set quality without resizing
  imageSize ? getResizedCanvas(canvas, imageSize) : canvas,
  imageSize?.quality ?? 1,
  imageType,
];

/* istanbul ignore next */
export const canvasToArrayBufferResized = async (
  canvas: HTMLCanvasElement,
  imageSize?: ImageSize,
  imageType: SupportedImageTypes = DEFAULT_IMAGE_FORMAT,
): Promise<ArrayBuffer> =>
  canvasToArrayBuffer(
    ...getCanvasToArrayBufferArguments(
      canvas,
      imageSize,
      getResizedCanvas,
      imageType,
    ),
  );

export type GetResizedCanvas = (
  canvas: HTMLCanvasElement,
  imageSize: ImageSize,
) => HTMLCanvasElement;
/* istanbul ignore next */
const getResizedCanvas: GetResizedCanvas = (canvas, imageSize) => {
  const canvasNewSize = document.createElement('canvas');
  canvasNewSize.width = imageSize.width;
  canvasNewSize.height = imageSize.height;
  canvasNewSize
    .getContext('2d')
    ?.drawImage(...getDrawImageArguments(canvas, imageSize));

  return canvasNewSize;
};
/* istanbul ignore next */
export const getArrayBufferFromImageData = async (
  imageData: ImageData,
  imageSize?: ImageSize,
  imageType: SupportedImageTypes = DEFAULT_IMAGE_FORMAT,
): Promise<ArrayBuffer> => {
  const canvas = getCanvasFromImageData(imageData);
  return canvasToArrayBufferResized(canvas, imageSize, imageType);
};

/* istanbul ignore next */
export const getArrayBufferFromVideoElement = async (
  videoElement: HTMLVideoElement,
  imageSize?: ImageSize,
  imageType: SupportedImageTypes = DEFAULT_IMAGE_FORMAT,
): Promise<ArrayBuffer> => {
  const canvas = getCanvasFromVideoElement(videoElement);
  return canvasToArrayBufferResized(canvas, imageSize, imageType);
};
